import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth, defaultRouteFor } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import LogoBrand from "../components/LogoBrand";
import LanguageSwitch from "../components/LanguageSwitch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";

export default function ChangePassword() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const mustChange = !!(user && user.must_change_password);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 10) { toast.error(t("auth.pwd_min10")); return; }
    if (pwd === current) { toast.error(t("auth.pwd_same_as_current")); return; }
    if (pwd !== confirm) { toast.error(t("auth.pwd_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      toast.success(t("auth.pwd_changed_ok"));
      await refresh();
      // After forced change, land on the role's default page
      nav(defaultRouteFor(user?.role) || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("auth.something_went_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative" style={{
        backgroundImage: "url(https://images.unsplash.com/photo-1629140727571-9b5c6f6267b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMHJvb20lMjBjbGVhbnxlbnwwfHx8fDE3ODU5MTcwMDd8MA&ixlib=rb-4.1.0&q=85)",
        backgroundSize: "cover", backgroundPosition: "center",
      }}>
        <div className="absolute inset-0 bg-slate-900/40" />
      </div>
      <div className="flex flex-col items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <div className="[&_button]:!bg-white [&_button]:!text-slate-900 [&_button]:!border-slate-300">
              <LanguageSwitch variant="light" />
            </div>
          </div>
          <div className="flex flex-col items-center mb-8"><LogoBrand variant="login" /></div>
          <Card className="p-6 md:p-8 border-slate-200 shadow-sm" data-testid="change-password-card">
            <h1 className="font-serif text-2xl text-slate-900 mb-1">{t("auth.change_pwd_title")}</h1>
            <p className="text-sm text-slate-500 mb-6">
              {mustChange ? t("auth.first_login_pwd_help") : t("auth.change_pwd_help")}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="cur-pwd">{t("auth.current_password")}</Label>
                <Input id="cur-pwd" type="password" required value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="h-11 mt-1" data-testid="change-current-password" />
              </div>
              <div>
                <Label htmlFor="new-pwd">{t("auth.new_password")}</Label>
                <Input id="new-pwd" type="password" required minLength={10} value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="h-11 mt-1" data-testid="change-new-password"
                  placeholder="••••••••••" />
                <div className="text-xs text-slate-500 mt-1">{t("auth.pwd_min10")}</div>
              </div>
              <div>
                <Label htmlFor="confirm-pwd">{t("auth.confirm_password")}</Label>
                <Input id="confirm-pwd" type="password" required minLength={10} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11 mt-1" data-testid="change-confirm-password"
                  placeholder="••••••••••" />
              </div>
              <Button type="submit" disabled={busy}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="change-submit">
                {busy ? t("auth.saving") : t("auth.change_pwd_action")}
              </Button>
              {mustChange && (
                <Button type="button" variant="outline" className="w-full"
                  onClick={async () => { await logout(); nav("/login", { replace: true }); }}
                  data-testid="change-signout">
                  {t("auth.sign_out")}
                </Button>
              )}
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
